import { Fragment, useEffect, useMemo, useState } from "react";
import withMonthlyPaymentsAccess from "../components/withMonthlyPaymentsAccess";
import RadioGroup from "../../../DesignSystem/Inputs/RadioGroup";
import FileUpload from "../../../DesignSystem/Upload/FileUpload";
import UploadedFileList from "../../../DesignSystem/Upload/UploadedFileList";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import { useAdviserList } from "../../../../context/Adviser/Utils/AdviserEmailList";
import { ReactComponent as EditIcon } from "../../../../assets/icons/finance/Edit.svg";
import { ReactComponent as SortIcon } from "../../../../assets/icons/paraplanningDashboard/Sort.svg";
import {
  normaliseQuilterRows,
  parseQuilterStatementFile,
  QUILTER_TABLE_HEADERS,
} from "../utils/quilterStatementParser";
import {
  saveQuilterAdviserLinks,
  saveQuilterStatement,
  subscribeQuilterStatement,
} from "../services/quilterStatementService";
import { formatMonthKey } from "../utils/chargeDates";
import { downloadStatementTemplate, QUILTER_TEMPLATE_HEADERS } from "../utils/statementTemplates";

const normaliseName = (value) => String(value ?? "").trim().toLowerCase();

const QuilterSummary = ({ selectedMonth, monthOptions, onMonthChange }) => {
  const { advisers } = useAdviserList();
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [editingAdviser, setEditingAdviser] = useState("");
  const [savingAdviser, setSavingAdviser] = useState(false);
  const [adviserSaveError, setAdviserSaveError] = useState("");
  const [sort, setSort] = useState({ key: "", direction: 1 });

  const selectedMonthLabel = useMemo(
    () => formatMonthKey(selectedMonth),
    [selectedMonth],
  );
  const tableStatement = useMemo(() => {
    if (!statement) return null;
    const adviserLinks = new Map((statement.adviserLinks || []).map((link) => [normaliseName(link.sourceAdviserName), link]));
    const advisersByName = new Map(advisers.map((adviser) => [normaliseName(adviser.name), adviser]));
    const advisersByEmail = new Map(advisers.map((adviser) => [normaliseName(adviser.email), adviser]));
    const rows = normaliseQuilterRows(statement.rows).map((row) => {
      const override = adviserLinks.get(normaliseName(row.SourceAdviserName));
      const linkedAdviser = override?.email
        ? advisersByEmail.get(normaliseName(override.email)) || override
        : advisersByName.get(normaliseName(row.MappedAdviserName));
      return {
        ...row,
        LinkedAdviser: linkedAdviser?.email
          ? `${linkedAdviser.name || row.MappedAdviserName} (${linkedAdviser.email})`
          : `${row.MappedAdviserName || "Unmapped"} (email not linked)`,
        LinkedAdviserEmail: linkedAdviser?.email || "",
      };
    });
    return { ...statement, headers: QUILTER_TABLE_HEADERS, rows };
  }, [advisers, statement]);
  const formatHeader = (header) => ({
    LinkedAdviser: "Mapped Adviser",
    PolicyAdviser: "Selling Adviser",
    PolicyAccountNumber: "Policy Number",
  }[header] || String(header).replace(/([a-z0-9])([A-Z])/g, "$1 $2"));
  const formatCellValue = (row, header, rowIndex) => {
    if (header === "Index") return rowIndex + 1;
    if (header === "BusinessType") {
      const sourcePlanType = String(row.QuilterPlanType || "").trim();
      const showSource = sourcePlanType &&
        normaliseName(sourcePlanType) !== normaliseName(row.BusinessType);
      return (
        <div className="fit-column gap-xs">
          <span>{row.BusinessType || "-"}</span>
          {showSource ? (
            <span className="FP_Help_Text text-grey">{sourcePlanType}</span>
          ) : null}
        </div>
      );
    }
    if (header !== "PaymentAmount" && header !== "GrossReceived") return row[header] || "-";
    const rawAmount = String(row[header] ?? "").trim();
    if (!rawAmount) return "-";
    const amount = Number(rawAmount.replace(/[,£]/g, ""));
    return Number.isFinite(amount)
      ? amount.toLocaleString("en-GB", {
          style: "currency",
          currency: "GBP",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : row[header] || "-";
  };
  const isAmountColumn = (header) => header === "PaymentAmount" || header === "GrossReceived";

  const adviserOptions = useMemo(() => advisers
    .map((adviser) => ({ id: adviser.email, name: `${adviser.name} (${adviser.email})` }))
    .sort((left, right) => left.name.localeCompare(right.name, "en-GB")), [advisers]);
  const statementTotals = useMemo(() => {
    const numberValue = (value) => {
      const number = Number(String(value ?? "").replace(/[,£]/g, ""));
      return Number.isFinite(number) ? number : 0;
    };
    return (tableStatement?.rows || []).reduce((totals, row) => ({
      PaymentAmount: totals.PaymentAmount + numberValue(row.PaymentAmount),
      GrossReceived: totals.GrossReceived + numberValue(row.GrossReceived),
    }), { PaymentAmount: 0, GrossReceived: 0 });
  }, [tableStatement]);
  const sortedStatementRows = useMemo(() => {
    if (!tableStatement?.rows || !sort.key) return tableStatement?.rows || [];
    const numericColumns = new Set(["Index", "PaymentAmount", "GrossReceived"]);
    const comparable = (row) => {
      const value = row[sort.key];
      if (numericColumns.has(sort.key)) {
        const number = Number(String(value ?? "").replace(/[,£]/g, ""));
        return Number.isFinite(number) ? number : 0;
      }
      if (sort.key === "PaymentCreated") {
        const timestamp = Date.parse(String(value ?? ""));
        return Number.isNaN(timestamp) ? 0 : timestamp;
      }
      return String(value ?? "").toLocaleLowerCase("en-GB");
    };
    return [...tableStatement.rows].sort((left, right) => {
      const leftValue = comparable(left);
      const rightValue = comparable(right);
      if (typeof leftValue === "number" && typeof rightValue === "number") {
        return (leftValue - rightValue) * sort.direction;
      }
      return String(leftValue).localeCompare(String(rightValue), "en-GB", { numeric: true }) * sort.direction;
    });
  }, [sort, tableStatement]);
  const sortBy = (key) => {
    setEditingAdviser("");
    setSort((current) => ({
      key,
      direction: current.key === key ? -current.direction : 1,
    }));
  };
  const formatCurrency = (value) => Number(value).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const changeLinkedAdviser = async (row, email) => {
    const adviser = advisers.find((item) => normaliseName(item.email) === normaliseName(email));
    if (!adviser) return;
    setSavingAdviser(true);
    setAdviserSaveError("");
    const nextLinks = (statement.adviserLinks || [])
      .filter((link) => normaliseName(link.sourceAdviserName) !== normaliseName(row.SourceAdviserName));
    nextLinks.push({
      sourceAdviserName: row.SourceAdviserName,
      adviserName: adviser.name,
      email: adviser.email,
    });
    try {
      await saveQuilterAdviserLinks(selectedMonth, nextLinks);
      setEditingAdviser("");
    } catch {
      setAdviserSaveError("Unable to update the linked adviser email.");
    } finally {
      setSavingAdviser(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setLoadError("");
    setStatement(null);
    return subscribeQuilterStatement(
      selectedMonth,
      (nextStatement) => {
        setStatement(nextStatement);
        setLoading(false);
      },
      () => {
        setLoadError("Unable to load the Quilter statement.");
        setLoading(false);
      },
    );
  }, [selectedMonth]);

  const closeUpload = () => {
    if (uploading) return;
    setIsUploadOpen(false);
    setSelectedFile(null);
    setUploadError("");
  };

  const uploadStatement = async () => {
    setUploading(true);
    setUploadError("");
    try {
      const parsed = await parseQuilterStatementFile(selectedFile);
      await saveQuilterStatement({
        month: selectedMonth,
        file: selectedFile,
        previousStoragePath: statement?.storagePath || "",
        ...parsed,
      });
      setUploading(false);
      setIsUploadOpen(false);
      setSelectedFile(null);
    } catch (error) {
      setUploadError(error.message || "Unable to import the selected statement.");
      setUploading(false);
    }
  };

  return (
    <section aria-label="Quilter summary" data-month={selectedMonth} className="column gap-l quilter-summary">
      <header className="row space-between align-center gap-l">
        <h3 className="FP_H3">Quilter Summary</h3>
        <div className="row align-center gap-s">
          <SecondaryButton
            type="button"
            label="DOWNLOAD TEMPLATE"
            onClick={() => downloadStatementTemplate({
              headers: QUILTER_TEMPLATE_HEADERS,
              fileName: "Quilter_Statement_Template.xlsx",
              sheetName: "Quilter Statement",
            })}
          />
          <PrimaryButton
            type="button"
            label={statement ? "REUPLOAD QUILTER STATEMENT" : "UPLOAD QUILTER STATEMENT"}
            onClick={() => setIsUploadOpen(true)}
          />
        </div>
      </header>
      <div role="group" aria-label="Quilter Summary month">
        <RadioGroup
          name="quilter-summary-month"
          options={monthOptions}
          value={selectedMonth}
          onChange={(event) => onMonthChange(event.target.value)}
          inline
        />
      </div>

      {loading && <p className="FP_Content" role="status">Loading Quilter statement...</p>}
      {!loading && loadError && <p className="FP_Content text-error" role="alert">{loadError}</p>}
      {adviserSaveError && <p className="FP_Content text-error" role="alert">{adviserSaveError}</p>}
      {!loading && !loadError && !statement && (
        <p className="FP_Content" role="status">No Quilter statement has been uploaded for {selectedMonthLabel}.</p>
      )}
      {!loading && statement && (
        <>
          <p className="FP_Help_Text black">
            {tableStatement.fileUrl ? (
              <a
                className="text-primary-1 quilter-file-link"
                href={tableStatement.fileUrl}
                download={tableStatement.fileName}
              >
                {tableStatement.fileName}
              </a>
            ) : tableStatement.fileName} · {tableStatement.rowCount ?? tableStatement.rows?.length ?? 0} entries
          </p>
          <section className="website-dashboard-card table-scroll-card quilter-statement-card">
            <div className="website-table-wrap table-rows-scroll-container table-scroll-container">
              <table className="website-table quilter-statement-table" aria-label={`Quilter statement for ${selectedMonthLabel}`}>
                <thead><tr>{tableStatement.headers.map((header) => (
                  <Fragment key={header}>
                    <th
                      className={`FP_Help_Text_Semibold uppercase ${isAmountColumn(header) ? "text-align-right" : ""}`}
                      aria-sort={sort.key === header ? (sort.direction === 1 ? "ascending" : "descending") : "none"}
                    >
                      {header === "Index" ? formatHeader(header) : (
                        <button
                          type="button"
                          className={`table-heading-content table-sort-button ${isAmountColumn(header) ? "justify-flex-end" : ""}`}
                          onClick={() => sortBy(header)}
                        >
                          {formatHeader(header)}
                          <SortIcon aria-hidden="true" />
                        </button>
                      )}
                    </th>
                    {header === "Index" && <th className="FP_Help_Text_Semibold uppercase quilter-action-column">Edit</th>}
                  </Fragment>
                ))}</tr></thead>
                <tbody>{sortedStatementRows.map((row, rowIndex) => (
                  <Fragment key={`${rowIndex}-${row[tableStatement.headers[0]] || "row"}`}>
                    <tr className={!row.LinkedAdviserEmail ? "monthly-payment-unlinked-email-row" : ""}>
                      {tableStatement.headers.map((header) => (
                        <Fragment key={header}>
                          <td className={`FP_Help_Text black ${isAmountColumn(header) ? "text-align-right" : ""}`}>
                            {header === "LinkedAdviser"
                              ? <span className="quilter-linked-adviser">{row.LinkedAdviser}</span>
                              : formatCellValue(row, header, rowIndex)}
                          </td>
                          {header === "Index" && (
                            <td className="quilter-action-column">
                              <button
                                type="button"
                                className="table-row-action"
                                aria-label={`Edit linked adviser for ${row.SourceAdviserName}`}
                                aria-expanded={editingAdviser === rowIndex}
                                onClick={() => setEditingAdviser((current) => current === rowIndex ? "" : rowIndex)}
                              >
                                <EditIcon aria-hidden="true" />
                              </button>
                            </td>
                          )}
                        </Fragment>
                      ))}
                    </tr>
                    {editingAdviser === rowIndex && (
                      <tr className="quilter-edit-row">
                        <td colSpan={tableStatement.headers.length + 1}>
                          <div className="quilter-adviser-edit">
                            <span className="FP_Help_Text_Semibold">Linked adviser for {row.SourceAdviserName}</span>
                            <CustomSelect
                              options={adviserOptions}
                              value={row.LinkedAdviserEmail}
                              placeholder="Select adviser email"
                              disabled={savingAdviser}
                              onChange={(event) => changeLinkedAdviser(row, event.target.value)}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}</tbody>
                <tfoot>
                  <tr>{tableStatement.headers.map((header) => (
                    <Fragment key={header}>
                      <td className={`FP_Help_Text_Semibold ${isAmountColumn(header) ? "text-align-right" : ""}`}>
                        {header === "Index"
                          ? "Total"
                          : isAmountColumn(header)
                            ? formatCurrency(statementTotals[header])
                            : ""}
                      </td>
                      {header === "Index" && <td className="quilter-action-column"></td>}
                    </Fragment>
                  ))}</tr>
                </tfoot>
              </table>
            </div>
          </section>
        </>
      )}

      {isUploadOpen && (
        <div
          className="quilter-upload-overlay"
          role="presentation"
          onMouseDown={(event) => event.target === event.currentTarget && closeUpload()}
        >
          <div className="quilter-upload-modal column gap-l" role="dialog" aria-modal="true" aria-labelledby="quilter-upload-title">
            <div className="row space-between align-center gap-m">
              <div>
                <h3 id="quilter-upload-title" className="FP_H3">{statement ? "Reupload" : "Upload"} Quilter statement</h3>
                <p className="FP_Content">This statement will be saved against {selectedMonthLabel}.</p>
              </div>
              <button type="button" className="quilter-upload-close" onClick={closeUpload} aria-label="Close upload dialog">×</button>
            </div>
            <FileUpload
              label="Statement file"
              description="Drag and drop a CSV or Excel file here or"
              browseText="Browse files"
              accept=".csv,.xlsx,.xls"
              fileName={selectedFile?.name}
              files={selectedFile ? [selectedFile] : []}
              onFilesChange={(file) => {
                setSelectedFile(file);
                setUploadError("");
              }}
              disabled={uploading}
              status={uploadError ? "error" : "default"}
              statusMessage={uploadError}
            />
            {selectedFile && <UploadedFileList files={[selectedFile]} onRemove={() => setSelectedFile(null)} />}
            <div className="row justify-flex-end gap-m">
              {/* <SecondaryButton label="CANCEL" onClick={closeUpload} disabled={uploading} /> */}
              <PrimaryButton
                type="button"
                label={uploading ? "IMPORTING..." : statement ? "REUPLOAD" : "UPLOAD"}
                onClick={uploadStatement}
                disabled={!selectedFile || uploading}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
export default withMonthlyPaymentsAccess(QuilterSummary);
